import type { ReactNode } from 'react';

import { memo } from 'react';
import { useTranslation } from 'react-i18next';

import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TabPanel from '@mui/lab/TabPanel';
import TabContext from '@mui/lab/TabContext';
import { Box } from '@mui/material';

import { Iconify } from 'src/components/iconify';

export type VisitAllocationTabValue = 'available' | 'shuffle' | 'excluded';

interface VisitAllocationContentTabsProps {
  availableContent: ReactNode;
  shuffleContent: ReactNode;
  excludedContent: ReactNode;
  showShuffleTab?: boolean;
  activeTab: VisitAllocationTabValue;
  onActiveTabChange: (tab: VisitAllocationTabValue) => void;
}

const tabPanelSx = {
  flex: 1,
  minHeight: 0,
  p: 0,
  m: 0,
  display: 'flex',
  flexDirection: 'column',
};

export const VisitAllocationContentTabs = memo(
  ({
    availableContent,
    shuffleContent,
    excludedContent,
    showShuffleTab = false,
    activeTab,
    onActiveTabChange,
  }: VisitAllocationContentTabsProps) => {
    const { t } = useTranslation('activities');

    return (
      <TabContext value={activeTab}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
          }}
        >
          <Box
            sx={{
              bgcolor: 'background.neutral',
              borderBottom: '1px solid',
              borderColor: 'divider',
              px: 2,
            }}
          >
            <Tabs
              value={activeTab}
              onChange={(_, value: VisitAllocationTabValue) => onActiveTabChange(value)}
              sx={{
                minHeight: 48,
                bgcolor: 'transparent',
                '& .MuiTabs-flexContainer': {
                  gap: 5,
                },
                '& .MuiTab-root': {
                  minHeight: 48,
                  px: 0,
                  gap: 1,
                },
              }}
            >
              <Tab
                value="available"
                icon={<Iconify icon="solar:users-group-rounded-bold" width={24} />}
                iconPosition="start"
                label={t('allocation.details.tabs.available')}
              />
              {showShuffleTab ? (
                <Tab
                  value="shuffle"
                  icon={<Iconify icon="mdi:shuffle" width={24} />}
                  iconPosition="start"
                  label={t('allocation.details.tabs.shuffle')}
                />
              ) : null}
              <Tab
                value="excluded"
                icon={<Iconify icon="solar:user-block-rounded-bold" width={24} />}
                iconPosition="start"
                label={t('allocation.details.tabs.excluded')}
              />
            </Tabs>
          </Box>

          {activeTab === 'available' ? (
            <TabPanel value="available" sx={tabPanelSx}>
              {availableContent}
            </TabPanel>
          ) : null}

          {showShuffleTab && activeTab === 'shuffle' ? (
            <TabPanel value="shuffle" sx={{ ...tabPanelSx, overflow: 'auto' }}>
              {shuffleContent}
            </TabPanel>
          ) : null}

          {activeTab === 'excluded' ? (
            <TabPanel value="excluded" sx={tabPanelSx}>
              {excludedContent}
            </TabPanel>
          ) : null}
        </Box>
      </TabContext>
    );
  }
);

VisitAllocationContentTabs.displayName = 'VisitAllocationContentTabs';
